import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LayoutDashboard, Package, MapPin, User, Heart, Clock, LogOut, Loader2 } from "lucide-react";
import EmptyState from "@/components/common/EmptyState";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nProvider";
import * as auth from "@/api/authClient";

// Account area backed by REAL WordPress data: profile and orders come from the
// KHA Auth /me endpoint (actual WP customer account + actual WooCommerce orders).

const STATUS_KEYS = ["st_received", "st_processing", "st_purchased", "st_preparing", "st_shipped", "st_in_transit", "st_delivered", "st_cancelled", "st_refunded"];

// WooCommerce core statuses -> the storefront status vocabulary. ZQ-specific
// milestones (purchased from supplier, preparing shipment, in transit) appear
// automatically once the ZQ fulfillment sync reports them.
const WP_STATUS_MAP = {
  pending: "st_received",
  processing: "st_processing",
  "on-hold": "st_processing",
  completed: "st_delivered",
  cancelled: "st_cancelled",
  refunded: "st_refunded",
  failed: "st_cancelled",
};

function OrdersView() {
  const { t } = useI18n();
  const { refreshUser } = useApp();
  const [orders, setOrders] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const d = await refreshUser();
        setOrders(d.orders || []);
      } catch (e) {
        setError(e.message || t("auth_failed_generic"));
        setOrders([]);
      }
    })();
  }, []); // eslint-disable-line

  if (orders === null)
    return (
      <div className="grid place-items-center py-16 text-muted-foreground">
        <Loader2 className="animate-spin" size={28} />
      </div>
    );

  if (error) return <p className="rounded-lg bg-destructive/10 p-3 text-xs font-medium text-destructive">{error}</p>;

  if (!orders.length)
    return (
      <div>
        <div className="mb-5 flex flex-wrap gap-2">
          {STATUS_KEYS.map((k) => (
            <span key={k} className="rounded-full bg-secondary px-3 py-1 text-xs font-medium text-muted-foreground">{t(k)}</span>
          ))}
        </div>
        <EmptyState icon={Package} title={t("no_orders_title")} body={t("no_orders_body")} action={<Link to="/shop" className="rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white">{t("continue_shopping")}</Link>} />
      </div>
    );

  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} className="rounded-2xl border border-border p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3">
              <span className="font-display font-bold text-ink">#{o.number}</span>
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-ink" data-testid={`order-status-${o.id}`}>
                {t(WP_STATUS_MAP[o.status] || "st_processing")}
              </span>
            </div>
            <div className="text-sm text-muted-foreground">{o.date}</div>
          </div>
          {!!o.items?.length && (
            <ul className="mt-3 space-y-1 text-sm text-muted-foreground">
              {o.items.map((it, i) => (
                <li key={i}>{it.name} × {it.qty}</li>
              ))}
            </ul>
          )}
          <div className="mt-3 text-sm font-bold text-ink">{o.total} {o.currency}</div>
          <Link to="/track" className="mt-2 inline-block text-xs font-semibold text-brand hover:underline">{t("track_order")} →</Link>
        </div>
      ))}
    </div>
  );
}

function AddressesView() {
  const { t } = useI18n();
  const [adding, setAdding] = useState(false);
  const F = "h-11 w-full rounded-lg border border-border px-3 text-sm outline-none focus:border-brand";
  return (
    <div>
      {!adding ? (
        <EmptyState icon={MapPin} title={t("addresses")} body={t("no_products_body")} action={<button onClick={() => setAdding(true)} className="flex items-center gap-2 rounded-full bg-ink px-6 py-2.5 text-sm font-semibold text-white">{t("addresses")}</button>} />
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); setAdding(false); }} className="grid max-w-lg gap-3 sm:grid-cols-2">
          <input placeholder={t("full_name")} className={`${F} sm:col-span-2`} />
          <input placeholder={t("address_line")} className={`${F} sm:col-span-2`} />
          <input placeholder={t("country")} className={F} />
          <input placeholder={t("city")} className={F} />
          <input placeholder={t("postal_code")} className={F} />
          <input placeholder={t("phone")} className={F} />
          <button className="h-11 rounded-full bg-brand text-sm font-bold text-brand-foreground sm:col-span-2">{t("apply")}</button>
        </form>
      )}
    </div>
  );
}

function ProfileView() {
  const { t } = useI18n();
  const { user, refreshUser } = useApp();
  const [form, setForm] = useState({ name: "", current_password: "", new_password: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name }));
  }, [user]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setError(""); setDone(false); setBusy(true);
    try {
      await auth.updateProfile({
        name: form.name,
        current_password: form.current_password || undefined,
        new_password: form.new_password || undefined,
      });
      await refreshUser();
      setDone(true);
      setForm((f) => ({ ...f, current_password: "", new_password: "" }));
    } catch (err) {
      setError(err.message || t("auth_failed_generic"));
    } finally {
      setBusy(false);
    }
  };

  const F = "h-11 w-full rounded-lg border border-border px-3 text-sm outline-none focus:border-brand";
  return (
    <form onSubmit={save} className="grid max-w-lg gap-4">
      <div><label className="mb-1 block text-sm font-medium">{t("name")}</label><input value={form.name} onChange={set("name")} className={F} data-testid="profile-name" /></div>
      <div><label className="mb-1 block text-sm font-medium">{t("email")}</label><input value={user?.email || ""} disabled className={`${F} opacity-60`} data-testid="profile-email" /></div>
      <div><label className="mb-1 block text-sm font-medium">{t("current_password")}</label><input type="password" value={form.current_password} onChange={set("current_password")} placeholder="••••••••" className={F} /></div>
      <div><label className="mb-1 block text-sm font-medium">{t("new_password")}</label><input type="password" value={form.new_password} onChange={set("new_password")} placeholder="••••••••" className={F} /></div>
      {error && <p className="rounded-lg bg-destructive/10 p-3 text-xs font-medium text-destructive">{error}</p>}
      {done && <p className="rounded-lg bg-success/10 p-3 text-xs font-medium text-success">{t("profile_saved")}</p>}
      <button disabled={busy} type="submit" className="h-11 w-fit rounded-full bg-brand px-6 text-sm font-bold text-brand-foreground disabled:opacity-60">{t("apply")}</button>
    </form>
  );
}

function Dashboard({ orderCount }) {
  const { t } = useI18n();
  const { user, wishlist, recentlyViewed } = useApp();
  const cards = [
    [t("orders"), orderCount ?? 0, Package, "/account/orders"],
    [t("wishlist"), wishlist.length, Heart, "/wishlist"],
    [t("recently_viewed"), recentlyViewed.length, Clock, "/"],
  ];
  return (
    <div>
      <div className="mb-6 rounded-2xl bg-ink p-6 text-white">
        <p className="text-sm text-white/60">{t("welcome_back")}</p>
        <p className="font-display text-2xl font-bold">{user?.name}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {cards.map(([label, count, Icon, to]) => (
          <Link key={label} to={to} className="rounded-2xl border border-border p-5 hover-lift">
            <Icon className="text-brand" size={22} />
            <div className="mt-3 font-display text-2xl font-bold text-ink">{count}</div>
            <div className="text-sm text-muted-foreground">{label}</div>
          </Link>
        ))}
      </div>
    </div>
  );
}

const NAV = [
  ["dashboard", "/account", LayoutDashboard, "dashboard"],
  ["orders", "/account/orders", Package, "my_orders"],
  ["addresses", "/account/addresses", MapPin, "addresses"],
  ["profile", "/account/profile", User, "profile"],
];

export default function AccountPage({ tab = "dashboard" }) {
  const { t } = useI18n();
  const { user, authReady, logout, refreshUser } = useApp();
  const [orderCount, setOrderCount] = useState(null);

  useEffect(() => {
    if (!user) return;
    if (tab !== "dashboard") return;
    refreshUser().then((d) => setOrderCount((d.orders || []).length)).catch(() => setOrderCount(0));
  }, [user, tab]); // eslint-disable-line

  if (!authReady)
    return (
      <div className="grid place-items-center py-24 text-muted-foreground">
        <Loader2 className="animate-spin" size={28} />
      </div>
    );
  if (!user) return <Navigate to="/auth" replace />;

  const titles = { dashboard: t("dashboard"), orders: t("my_orders"), addresses: t("addresses"), profile: t("profile") };

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <div className="grid gap-8 lg:grid-cols-[240px_1fr]">
        <aside>
          <div className="rounded-2xl border border-border p-3">
            {NAV.map(([section, to, Icon, labelKey]) => {
              const active = tab === section;
              return (
                <Link key={section} to={to} className={`mb-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition ${active ? "bg-ink text-white" : "hover:bg-secondary"}`} data-testid={`account-nav-${section}`}>
                  <Icon size={18} /> {t(labelKey)}
                </Link>
              );
            })}
            <button onClick={logout} className="mt-1 flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium text-destructive hover:bg-destructive/5" data-testid="logout-btn">
              <LogOut size={18} /> {t("logout")}
            </button>
          </div>
        </aside>

        <div>
          <h1 className="mb-6 font-display text-2xl font-bold text-ink">{titles[tab]}</h1>
          {tab === "dashboard" && <Dashboard orderCount={orderCount} />}
          {tab === "orders" && <OrdersView />}
          {tab === "addresses" && <AddressesView />}
          {tab === "profile" && <ProfileView />}
        </div>
      </div>
    </div>
  );
}
